import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { DateTime, Settings } from 'luxon';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Chip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import GroupIcon from '@mui/icons-material/Group';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import { useCart } from '../contexts/CartContext';
import {
  CHAMPS_APPRENANT,
  COURRIEL_VALIDE,
  compteur,
  erreurPendantSaisie,
  erreursFormulaireApprenant,
  apprenantPourServeur,
  verifierAupresDuServeur,
  erreursServeurParChamp,
  defilerVersPremiereErreur,
} from '../utils/reglesInscription';

// Champs du formulaire de session privée (la personne s'inscrit elle-même).
const CHAMPS_PRIVES = ['nom', 'prenom', 'email', 'telephone', 'adressepostale', 'numero_appartement', 'ville', 'province', 'code_postal'];

Settings.defaultZone = 'utc';

// Pas d'étape « Confirmation » : le bouton de la page Validation vérifie,
// ajoute au panier et ouvre le panier directement.
const steps = ['Accès', 'Informations', 'Validation'];

const provinces = [
  { value: 'QC', label: 'Québec' },
  { value: 'ON', label: 'Ontario' },
  { value: 'BC', label: 'Colombie-Britannique' },
  { value: 'AB', label: 'Alberta' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'NS', label: 'Nouvelle-Écosse' },
  { value: 'NB', label: 'Nouveau-Brunswick' },
  { value: 'NL', label: 'Terre-Neuve-et-Labrador' },
  { value: 'PE', label: 'Île-du-Prince-Édouard' },
  { value: 'NT', label: 'Territoires du Nord-Ouest' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'YT', label: 'Yukon' },
];

const InscriptionPrivee = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const formTopRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ajoutEnCours, setAjoutEnCours] = useState(false);

  // Session data
  const [session, setSession] = useState(null);
  const [sessionError, setSessionError] = useState('');

  // Code access
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeRabais, setCodeRabais] = useState(null); // { code, pourcentage }

  // Rabais saisi manuellement
  const [rabaisManuel, setRabaisManuel] = useState('');
  const [rabaisValide, setRabaisValide] = useState(null); // null | { code, pourcentage }
  const [rabaisError, setRabaisError] = useState('');

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adressepostale: '',
    numero_appartement: '',
    ville: '',
    province: 'QC',
    code_postal: '',
    date_naissance: '',
    accepte: false
  });

  const [formErrors, setFormErrors] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Charger la session privée au montage
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await axios.get(`${API_URL}/sessions/private/${token}`);
        if (res.data.success) {
          setSession(res.data.data);
        }
      } catch (err) {
        setSessionError(
          err.response?.data?.errors?.[0]?.message ||
          'Session d\'inscription introuvable ou expirée.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [token, API_URL]);

  useEffect(() => {
    if (formTopRef.current) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeStep]);

  // Vérifier le code d'accès
  const handleVerifyCode = async () => {
    if (!codeInput.trim()) {
      setCodeError('Veuillez saisir le code d\'accès.');
      return;
    }
    setCodeLoading(true);
    setCodeError('');
    try {
      const res = await axios.post(`${API_URL}/sessions/private/${token}/validate-code`, {
        code_securise: codeInput.trim().toUpperCase()
      });
      if (res.data.valid) {
        if (res.data.code_rabais) {
          setCodeRabais(res.data.code_rabais);
          setRabaisValide(res.data.code_rabais);
          setRabaisManuel(res.data.code_rabais.code);
        }
        setActiveStep(1);
      } else {
        setCodeError('Code d\'accès incorrect. Veuillez vérifier et réessayer.');
      }
    } catch {
      setCodeError('Erreur lors de la vérification. Veuillez réessayer.');
    } finally {
      setCodeLoading(false);
    }
  };

  // Valider manuellement un code rabais
  const handleValidateRabais = async () => {
    if (!rabaisManuel.trim()) {
      setRabaisValide(null);
      setRabaisError('');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/codes-rabais/valider`, { code: rabaisManuel.trim() });
      if (res.data.valid) {
        setRabaisValide(res.data.data);
        setRabaisError('');
      } else {
        setRabaisValide(null);
        setRabaisError('Code rabais invalide ou inactif.');
      }
    } catch {
      setRabaisError('Erreur lors de la validation du code.');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    // Rouge dès que la limite est dépassée, pendant la saisie.
    const erreur = type === 'checkbox' ? '' : erreurPendantSaisie(name, value);
    if (erreur || formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: erreur }));
  };

  // Courriel incomplet : signalé en quittant le champ.
  const handleEmailBlur = () => {
    const email = formData.email.trim();
    if (email && !formErrors.email && !COURRIEL_VALIDE.test(email)) {
      setFormErrors(prev => ({ ...prev, email: 'Courriel : adresse invalide (exemple : nom@domaine.ca)' }));
    }
  };

  // Erreur du champ, sinon compteur de caractères près de la limite.
  const aide = (champ) => formErrors[champ] || compteur(formData[champ], CHAMPS_APPRENANT[champ].max);

  const validateStep1 = () => {
    const errors = erreursFormulaireApprenant(formData, { champs: CHAMPS_PRIVES });
    setFormErrors(errors);
    const valide = Object.keys(errors).length === 0;
    if (!valide) defilerVersPremiereErreur();
    return valide;
  };

  const validateStep2 = () => {
    if (!formData.accepte) {
      setFormErrors({ accepte: 'Vous devez accepter les conditions' });
      return false;
    }
    return true;
  };

  const handleNextStep1 = () => {
    setError('');
    if (validateStep1()) setActiveStep(2);
  };

  // Page Validation : vérification par le serveur, ajout au panier et
  // ouverture du panier, en un seul clic.
  const handleConfirmerEtAjouter = async () => {
    if (!validateStep2()) {
      defilerVersPremiereErreur();
      return;
    }
    if (!session) return;

    setAjoutEnCours(true);
    setError('');
    try {
      const verification = await verifierAupresDuServeur(API_URL, [{
        apprenant: apprenantPourServeur(formData, true),
        session_id: session.id,
        pour_un_tiers: false,
        formationTitre: session.formation?.titre,
      }]);

      if (!verification.valide) {
        const { parChamp, autres } = erreursServeurParChamp(verification.articles[0]);
        if (Object.keys(parChamp).length > 0) {
          setFormErrors(parChamp);
          setError(autres.length ? autres.join(' ') : 'Certaines informations doivent être corrigées.');
          setActiveStep(1);
        } else {
          setError(autres.join(' '));
        }
        defilerVersPremiereErreur();
        setAjoutEnCours(false);
        return;
      }

      handleAddToCart();
    } catch (erreurVerification) {
      setError(erreurVerification.message);
      defilerVersPremiereErreur();
      setAjoutEnCours(false);
    }
  };

  const handleAddToCart = () => {
    if (!session) return;

    const prixOriginal = session.formation?.tarif || 0;
    const rabaisActif = rabaisValide;
    const prixFinal = rabaisActif
      ? prixOriginal * (1 - rabaisActif.pourcentage / 100)
      : prixOriginal;

    const cartItem = {
      formationId: session.formation?.id,
      sessionId: session.id,
      formationTitre: session.formation?.titre || 'Formation',
      sessionInfo: {
        ville: session.ville,
        adresse: session.adresse,
        date_debut: session.date_debut,
        date_fin: session.date_fin,
        heure_debut: session.heure_debut,
        heure_fin: session.heure_fin,
      },
      formation: {
        ...session.formation,
        tarif: prixFinal,
        tarif_original: prixOriginal,
      },
      formData: { ...formData },
      isForSelf: true,
      nom_du_groupe: session.groupeOrganisme?.organisme || null,
      rabais: rabaisActif ? { code: rabaisActif.code, pourcentage: rabaisActif.pourcentage } : null,
    };

    addToCart(cartItem);
    navigate('/panier');
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return DateTime.fromISO(d.split('T')[0]).toFormat('dd MMMM yyyy', { locale: 'fr' });
    } catch { return d; }
  };

  // ── Loading / Erreur session ──
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ color: '#c62828' }} />
        <Typography sx={{ mt: 2, color: '#666' }}>Chargement de la session…</Typography>
      </Container>
    );
  }

  if (sessionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{sessionError}</Alert>
        <Button component={Link} to="/" variant="outlined">Retour à l'accueil</Button>
      </Container>
    );
  }

  const prixOriginal = session?.formation?.tarif || 0;
  const prixFinal = rabaisValide ? prixOriginal * (1 - rabaisValide.pourcentage / 100) : prixOriginal;

  return (
    <Container maxWidth="md" sx={{ py: 4 }} ref={formTopRef}>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <GroupIcon sx={{ color: '#c62828', fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Inscription de groupe
          </Typography>
        </Box>

        {session && (
          <Paper sx={{ p: 2.5, backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#c62828', mb: 1 }}>
              {session.formation?.titre}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${session.ville}`} size="small" variant="outlined" />
              <Chip label={`${formatDate(session.date_debut)}`} size="small" variant="outlined" />
              <Chip label={`${session.heure_debut} – ${session.heure_fin}`} size="small" variant="outlined" />
              {session.groupeOrganisme && (
                <Chip
                  icon={<GroupIcon />}
                  label={session.groupeOrganisme.organisme}
                  size="small"
                  sx={{ backgroundColor: 'rgba(198,40,40,0.08)', color: '#c62828' }}
                />
              )}
              {rabaisValide && (
                <Chip
                  icon={<LocalOfferIcon />}
                  label={`Rabais −${rabaisValide.pourcentage}%`}
                  size="small"
                  sx={{ backgroundColor: '#fff8e1', color: '#f57f17' }}
                />
              )}
            </Box>
          </Paper>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── Étape 0 : Saisie du code d'accès ── */}
      {activeStep === 0 && (
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LockIcon sx={{ fontSize: 56, color: '#1565c0', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Code d'accès requis
            </Typography>
            <Typography color="textSecondary">
              Cette page est réservée aux participants invités. Saisissez le code d'accès reçu par email.
            </Typography>
          </Box>

          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            <TextField
              label="Code d'accès"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyCode(); }}
              fullWidth
              error={!!codeError}
              helperText={codeError}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 22, letterSpacing: 8, textAlign: 'center', textTransform: 'uppercase' } }}
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleVerifyCode}
              disabled={codeLoading}
              sx={{ backgroundColor: '#c62828', py: 1.5 }}
            >
              {codeLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Accéder au formulaire'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── Étape 1 : Informations personnelles ── */}
      {activeStep === 1 && (
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
            Vos informations personnelles
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Nom *"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                error={!!formErrors.nom}
                helperText={aide('nom')}
                fullWidth
              />
              <TextField
                label="Prénom *"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                error={!!formErrors.prenom}
                helperText={aide('prenom')}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Email *"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!formErrors.email}
                helperText={aide('email')}
                onBlur={handleEmailBlur}
                fullWidth
              />
              <TextField
                label="Téléphone *"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                error={!!formErrors.telephone}
                helperText={aide('telephone')}
                fullWidth
              />
            </Box>

            <TextField
              label="Date de naissance *"
              name="date_naissance"
              type="date"
              value={formData.date_naissance}
              onChange={handleChange}
              error={!!formErrors.date_naissance}
              helperText={formErrors.date_naissance}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Adresse postale *"
              name="adressepostale"
              value={formData.adressepostale}
              onChange={handleChange}
              error={!!formErrors.adressepostale}
              helperText={aide('adressepostale')}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="App / Unité (optionnel)"
                name="numero_appartement"
                value={formData.numero_appartement}
                onChange={handleChange}
                error={!!formErrors.numero_appartement}
                helperText={aide('numero_appartement')}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Ville *"
                name="ville"
                value={formData.ville}
                onChange={handleChange}
                error={!!formErrors.ville}
                helperText={aide('ville')}
                sx={{ flex: 2 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }} error={!!formErrors.province}>
                <InputLabel>Province *</InputLabel>
                <Select
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  label="Province *"
                >
                  {provinces.map(p => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
                {formErrors.province && <FormHelperText>{formErrors.province}</FormHelperText>}
              </FormControl>
              <TextField
                label="Code postal *"
                name="code_postal"
                value={formData.code_postal}
                onChange={handleChange}
                error={!!formErrors.code_postal}
                helperText={aide('code_postal')}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Code rabais */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferIcon fontSize="small" sx={{ color: '#f57f17' }} />
              Code rabais (optionnel)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Code rabais"
                value={rabaisManuel}
                onChange={(e) => { setRabaisManuel(e.target.value.toUpperCase()); setRabaisValide(null); setRabaisError(''); }}
                error={!!rabaisError}
                helperText={rabaisError}
                inputProps={{ style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
                fullWidth
                disabled={!!codeRabais}
              />
              <Button
                variant="outlined"
                onClick={handleValidateRabais}
                disabled={!!codeRabais || !rabaisManuel.trim()}
                sx={{ minWidth: 120, borderColor: '#f57f17', color: '#f57f17', '&:hover': { borderColor: '#e65100', backgroundColor: 'rgba(245,127,23,0.04)' } }}
              >
                Valider
              </Button>
            </Box>
            {rabaisValide && (
              <Alert severity="success" icon={<LocalOfferIcon />} sx={{ py: 0.5 }}>
                Rabais de <strong>−{rabaisValide.pourcentage}%</strong> appliqué.
                {prixOriginal > 0 && (
                  <> Prix : <s>{prixOriginal.toFixed(2)} $</s> → <strong>{prixFinal.toFixed(2)} $</strong></>
                )}
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleNextStep1}
              sx={{ backgroundColor: '#c62828', px: 4 }}
            >
              Continuer
            </Button>
          </Box>
        </Paper>
      )}

      {/* ── Étape 2 : Validation / Récapitulatif ── */}
      {activeStep === 2 && (
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
            Récapitulatif de votre inscription
          </Typography>

          <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1.5, p: 3, mb: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>Vos informations</Typography>
            {[
              ['Nom', `${formData.prenom} ${formData.nom}`],
              ['Email', formData.email],
              ['Téléphone', formData.telephone],
              ['Date de naissance', formatDate(formData.date_naissance)],
              ['Adresse', `${formData.adressepostale}${formData.numero_appartement ? `, App. ${formData.numero_appartement}` : ''}, ${formData.ville}, ${formData.province} ${formData.code_postal}`],
            ].map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #eee' }}>
                <Typography variant="body2" color="textSecondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: 1.5, p: 3, mb: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>Session</Typography>
            {[
              ['Formation', session?.formation?.titre],
              ['Groupe', session?.groupeOrganisme?.organisme],
              ['Date', formatDate(session?.date_debut)],
              ['Lieu', `${session?.adresse}, ${session?.ville}`],
            ].filter(([, v]) => v).map(([label, value]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #eee' }}>
                <Typography variant="body2" color="textSecondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, mt: 1 }}>
              <Typography variant="body2" color="textSecondary">Prix</Typography>
              <Box sx={{ textAlign: 'right' }}>
                {rabaisValide && (
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: '#999' }}>
                    {prixOriginal.toFixed(2)} $
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {prixFinal.toFixed(2)} $
                  {rabaisValide && <Chip label={`−${rabaisValide.pourcentage}%`} size="small" sx={{ ml: 1, backgroundColor: '#fff8e1', color: '#f57f17', height: 20 }} />}
                </Typography>
              </Box>
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                name="accepte"
                checked={formData.accepte}
                onChange={handleChange}
                sx={{ color: '#c62828', '&.Mui-checked': { color: '#c62828' } }}
              />
            }
            label="Je confirme que les informations sont exactes et j'accepte les conditions d'inscription."
          />
          {formErrors.accepte && (
            <FormHelperText error>{formErrors.accepte}</FormHelperText>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button variant="outlined" onClick={() => setActiveStep(1)}>Modifier</Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleConfirmerEtAjouter}
              disabled={ajoutEnCours}
              sx={{ backgroundColor: '#c62828', px: 4 }}
            >
              {ajoutEnCours ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Confirmer et ajouter au panier'}
            </Button>
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default InscriptionPrivee;
